// Trends Registry - Central configuration for all trend visualizations
// This allows us to easily add new trend types and data sources

import SecondarySchoolTrends from './SecondarySchoolTrends'
import PrimarySchoolTrends from './PrimarySchoolTrends'
import PLETrends from './PLETrends'
import InstitutionCountTrends from './InstitutionCountTrends'

// Registry of available trend visualizations
export const TRENDS_REGISTRY = {
  education: {
    'secondary-school-enrollment': {
      id: 'secondary-school-enrollment',
      title: 'Secondary School Enrollment',
      description: 'Enrollment trends for secondary schools (S1-S6)',
      component: SecondarySchoolTrends,
      dataSource: {
        type: 'csv',
        path: '/data/trends/kayunga_learners_enrolment_secondary.csv',
        // Data validation function
        validateRecord: record => {
          return (
            record.district &&
            record.year &&
            record.school_name &&
            ['Boys', 'Girls'].includes(record.gender)
          )
        },
        // Data filtering function - can be used to filter specific data subsets
        filterData: (data, filters = {}) => {
          return data.filter(record => {
            // Base validation
            if (!record.district || !record.year || !record.school_name) {
              return false
            }

            // Location filtering (handled by TrendsContext via location_code)
            if (filters.location_code) {
              if (
                !record.location_code ||
                !record.location_code.startsWith(filters.location_code)
              ) {
                return false
              }
            }

            return true
          })
        },
      },
      category: 'education',
      tags: ['enrollment', 'secondary', 'gender', 'schools'],
      priority: 1, // Display order
      enabled: true,
    },

    'primary-school-enrollment': {
      id: 'primary-school-enrollment',
      title: 'Primary School Enrollment',
      description: 'Enrollment trends for primary schools (P1-P7)',
      component: PrimarySchoolTrends,
      dataSource: {
        type: 'csv',
        path: '/data/trends/masindi_learners_enrolment_primary.csv',
        // Data validation function
        validateRecord: record => {
          return (
            record.district &&
            record.year &&
            record.school_name &&
            ['Boys', 'Girls'].includes(record.gender)
          )
        },
        // Data filtering function - can be used to filter specific data subsets
        filterData: (data, filters = {}) => {
          return data.filter(record => {
            // Base validation
            if (!record.district || !record.year || !record.school_name) {
              return false
            }

            // Location filtering (handled by TrendsContext via location_code)
            if (filters.location_code) {
              if (
                !record.location_code ||
                !record.location_code.startsWith(filters.location_code)
              ) {
                return false
              }
            }

            return true
          })
        },
      },
      category: 'education',
      tags: ['enrollment', 'primary', 'gender', 'schools'],
      priority: 2, // Display order (after secondary school trends)
      enabled: true,
    },

    'ple-performance': {
      id: 'ple-performance',
      title: 'PLE Performance Analysis',
      description:
        'Primary Leaving Examination performance trends by grade division',
      component: PLETrends,
      dataSource: {
        type: 'csv',
        path: '/data/trends/ple_analysis.csv',
        validateRecord: record => {
          return (
            record.district &&
            record.year &&
            record.school_name &&
            ['Male', 'Female'].includes(record.gender) &&
            [
              'Division 1',
              'Division 2',
              'Division 3',
              'Division 4',
              'Division U',
              'Division X',
            ].includes(record.grade)
          )
        },
        filterData: (data, filters = {}) => {
          return data.filter(record => {
            // Base validation
            if (!record.district || !record.year || !record.school_name) {
              return false
            }

            // Location filtering (handled by TrendsContext via location_code)
            if (filters.location_code) {
              if (
                !record.location_code ||
                !record.location_code.startsWith(filters.location_code)
              ) {
                return false
              }
            }

            // Ensure valid grade divisions
            const validGrades = [
              'Division 1',
              'Division 2',
              'Division 3',
              'Division 4',
              'Division U',
              'Division X',
            ]
            if (!validGrades.includes(record.grade)) {
              return false
            }

            // Ensure valid gender
            if (!['Male', 'Female'].includes(record.gender)) {
              return false
            }

            return true
          })
        },
      },
      category: 'education',
      tags: [
        'ple',
        'performance',
        'examination',
        'grade-division',
        'education-quality',
      ],
      priority: 3, // Display after enrollment trends
      enabled: true,
    },

    'institution-count': {
      id: 'institution-count',
      title: 'Educational Institution Count',
      description: 'Institution count trends by education level and ownership',
      component: InstitutionCountTrends,
      dataSource: {
        type: 'csv',
        path: '/data/trends/number_of_institutions.csv',
        validateRecord: record => {
          return (
            record.district &&
            record.year &&
            record.level &&
            ['Primary', 'Secondary', 'Tertiary'].includes(record.level) &&
            ['Government', 'Private'].includes(record.ownership) &&
            record.value !== undefined
          )
        },
        filterData: (data, filters = {}) => {
          return data.filter(record => {
            // Base validation
            if (!record.district || !record.year || !record.level) {
              return false
            }

            // Location filtering (handled by TrendsContext via location_code)
            if (filters.location_code) {
              if (
                !record.location_code ||
                !record.location_code.startsWith(filters.location_code)
              ) {
                return false
              }
            }

            // Ensure valid education levels
            if (!['Primary', 'Secondary', 'Tertiary'].includes(record.level)) {
              return false
            }

            // Ensure valid ownership types
            if (!['Government', 'Private'].includes(record.ownership)) {
              return false
            }

            // Ensure thematic_area is education
            if (record.thematic_area !== 'education') {
              return false
            }

            return true
          })
        },
      },
      category: 'education',
      tags: [
        'institutions',
        'infrastructure',
        'capacity',
        'government',
        'private',
        'education-access',
      ],
      priority: 4, // Display after PLE trends
      enabled: true,
    },

    // Future trend types can be added here:
    // 'teacher-student-ratios': { ... },
    // 'school-infrastructure': { ... },
  },

  health: {
    // Future health trends will go here:
    // 'facility-utilization': { ... },
    // 'service-coverage': { ... },
  },
}

// Helper function to get all trends for a category
export function getTrendsByCategory(category) {
  return Object.values(TRENDS_REGISTRY[category] || {})
    .filter(trend => trend.enabled)
    .sort((a, b) => a.priority - b.priority)
}

// Helper function to get a specific trend
export function getTrendById(category, trendId) {
  return TRENDS_REGISTRY[category]?.[trendId]
}

// Helper function to get all enabled categories
export function getAvailableCategories() {
  return Object.keys(TRENDS_REGISTRY).filter(category => {
    const trends = Object.values(TRENDS_REGISTRY[category] || {})
    return trends.some(trend => trend.enabled)
  })
}

// Data loader function for trends
export async function loadTrendData(trendConfig, filters = {}) {
  const { dataSource } = trendConfig

  if (dataSource.type === 'csv') {
    const response = await fetch(dataSource.path)
    if (!response.ok) {
      throw new Error(`Failed to load trend data: ${response.statusText}`)
    }

    const csvText = await response.text()

    // Parse CSV data
    const lines = csvText.trim().split('\n')
    const headers = lines[0].replace(/"/g, '').split(',')

    const data = lines.slice(1).map(line => {
      const values = line.split(',').map(val => val.replace(/"/g, '').trim())
      const row = {}
      headers.forEach((header, index) => {
        row[header] = values[index]
      })
      return row
    })

    // Apply data source filtering
    const filteredData = dataSource.filterData(data, filters)

    return filteredData
  }

  throw new Error(`Unsupported data source type: ${dataSource.type}`)
}
